import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
} from "chart.js";
import { Bar, Pie, Line, getElementAtEvent } from "react-chartjs-2";
import { useSelector } from "react-redux";
import store from "../../stores/store";
import PerfectScrollbar from "react-perfect-scrollbar";
import "react-perfect-scrollbar/dist/css/styles.css";
import { useEffect, useState } from "react";
import moment from "moment";
import { useRef } from "react";
import Zoom from "chartjs-plugin-zoom";
import { trans } from "../../lib/utils";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
  Zoom
);

function TimeDimensionPlayer() {
  const timeDimension = useSelector((state) => store.getState().timeDimension);
  const layers = useSelector((state) => store.getState().layers);

  const [chartData, setChartData] = useState(null);

  const chartRef = useRef(null);

  const zoomOptions = {
    limits: {
      x: { min: -200, max: 200, minRange: 50 },
      y: { min: -200, max: 200, minRange: 50 },
    },
    pan: {
      enabled: true,
      mode: "x",
    },
    zoom: {
      wheel: {
        enabled: true,
      },
      pinch: {
        enabled: true,
      },
      mode: "xy",
      onZoomComplete({ chart }) {
        // This update is needed to display up to date zoom level in the title.
        // Without this, previous zoom level is displayed.
        // The reason is: title uses the same beforeUpdate hook, and is evaluated before zoom.
        chart.update("none");
      },
    },
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      zoom: zoomOptions,
      legend: {
        position: "top",
        onClick: (e, a) => {
          let layerId = chartRef.current.data.datasets[a.datasetIndex].layerId;

          if (layerId) {
            store.dispatch({
              type: "timeDimension/layer/togglevisible",
              payload: layerId,
            });
          }

          return;
        },
        labels: {
          filter: (item) => {
            return item.text;
          },
        },
      },
    },
    scales: {
      y: {
        ticks: {
          display: false,
        },
      },
      x: {
        ticks: {
          maxRotation: 90,
          minRotation: 90,
          autoSkip: false,
        },
      },
    },
  };

  useEffect(() => {
    setChartData({
      labels: timeDimension.availableDimensions.map((el) => {
        return moment(el).format("DD MMM YYYY HH:mm");
      }),
      datasets: Object.keys(timeDimension.layers)
        .map((layerId, idx) => {
          return {
            data: timeDimension.availableDimensions.map((el) => {
              return timeDimension.layers[layerId]?.dimensions?.indexOf(el) >=
                0 && timeDimension.layers[layerId].visible
                ? idx + 1
                : null;
            }),
            hidden: !timeDimension.layers[layerId].visible ? true : false,
            label: layers[layerId].title,
            backgroundColor: timeDimension.colors[idx],
            layerId: layerId,
          };
        })
        .concat({
          type: "bar",
          data: timeDimension.availableDimensions.map((el) => {
            return Object.keys(timeDimension.layers).length + 1;
          }),
          backgroundColor: timeDimension.availableDimensions.map((el) => {
            return el === timeDimension.currentDimension
              ? "rgba(255, 101, 0, 0.3)"
              : "rgba(255, 101, 0, 0)";
          }),
        }),
    });
  }, [timeDimension]);

  return (
    <div className="mt-2">
      {chartData ? (
        <div>
          <button
            className="btn btn-sm btn-secondary text-white me-1 small"
            onClick={() => {
              store.dispatch({ type: "timeDimension/toggleplay" });
            }}
          >
            {window["timeDimension"] ? (
              <>
                <i className="fas fa-pause me-2 small"></i> {trans("Pause")}
              </>
            ) : (
              <>
                <i className="fas fa-play me-2 small"></i> {trans("Play")}
              </>
            )}
          </button>
          <button
            className="btn btn-sm btn-secondary text-white me-1 small"
            onClick={() => {
              store.dispatch({ type: "timeDimension/gotoprevious" });
            }}
          >
            <i className="fas fa-backward small"></i>
          </button>
          <button
            className="btn btn-sm btn-secondary text-white me-1 small"
            onClick={() => {
              store.dispatch({ type: "timeDimension/gotonext" });
            }}
          >
            <i className="fas fa-forward small"></i>
          </button>
          <button
            className="btn btn-sm btn-secondary text-white me-1 small"
            onClick={() => {
              store.dispatch({
                type: "timeDimension/interval",
                payload: timeDimension.interval + 500,
              });
            }}
          >
            {trans("Slower")}
          </button>
          <button
            className="btn btn-sm btn-secondary text-white me-1 small"
            onClick={() => {
              store.dispatch({
                type: "timeDimension/interval",
                payload:
                  timeDimension.interval <= 500
                    ? timeDimension.interval - 500
                    : 500,
              });
            }}
          >
            {trans("Faster")}
          </button>
          <span>
            {timeDimension.currentDimension
              ? moment(timeDimension.currentDimension).format(
                  "DD MMM YYYY HH:mm"
                )
              : ""}
          </span>
        </div>
      ) : (
        ""
      )}
      <PerfectScrollbar className="scrollbar-recaclulate">
        {chartData ? (
          <>
            <Line
              options={options}
              data={chartData}
              className="w-100"
              ref={chartRef}
              style={{ height: "calc(100% - 30px)" }}
              onClick={(event) => {
                let clickElement = getElementAtEvent(chartRef.current, event);

                if (clickElement[0]) {
                  store.dispatch({
                    type: "timeDimension/goto",
                    payload:
                      timeDimension.availableDimensions[clickElement[0].index],
                  });
                }
              }}
            />
          </>
        ) : (
          ""
        )}
      </PerfectScrollbar>
    </div>
  );
}

export default TimeDimensionPlayer;
